/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

// Vendored from light-grid-wapp app/components/ui/InfiniteList.tsx.
import { useCallback, useRef, type ReactNode } from "react";
import { Button } from "./Button";
import { LoadingOverlay } from "./LoadingOverlay";
import { Spinner } from "./Spinner";

interface InfiniteListProps {
  children: ReactNode;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  emptyState?: ReactNode;
  isEmpty: boolean;
  currentPage?: number;
  error?: string | null;
  onRetry?: () => void;
}

export function InfiniteList({
  children,
  isLoading,
  hasMore,
  onLoadMore,
  emptyState,
  isEmpty,
  currentPage = 1,
  error,
  onRetry,
}: InfiniteListProps) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || !hasMore || isLoading) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            onLoadMoreRef.current();
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      observerRef.current.observe(node);
    },
    [hasMore, isLoading]
  );

  if (isEmpty && isLoading) {
    return (
      <LoadingOverlay isLoading={true}>
        <div className="min-h-[400px]" />
      </LoadingOverlay>
    );
  }

  if (isEmpty && !isLoading) {
    return <div className="flex-1">{emptyState}</div>;
  }

  const showLoadMoreError = !!error && !isLoading;

  return (
    <div className="space-y-6">
      {children}

      {showLoadMoreError && (
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-sm text-text-secondary">Couldn't load more</p>
          {onRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      )}

      {!showLoadMoreError && hasMore && (
        <div
          ref={sentinelRef}
          className="h-20 flex items-center justify-center"
        >
          {isLoading && (
            <Spinner size="sm" />
          )}
        </div>
      )}

      {!hasMore && !isEmpty && currentPage > 1 && (
        <div className="text-center py-8 text-text-tertiary text-sm">
          You've reached the end
        </div>
      )}
    </div>
  );
}
